import { getBaseDomain, isLocalHostname } from "@/lib/hostname";

const DEFAULT_LOCAL_BACKEND_HTTPS_URL = "https://localhost:8004";
const DEFAULT_LOCAL_BACKEND_HTTP_URL = "http://localhost:8001";
const DEFAULT_LOCAL_GATEWAY_HTTPS_URL = "https://localhost:8013";
const DEFAULT_TENANT_HOST_TEMPLATE = "https://<tenant>.jewel-flow.com";
const KNOWN_LOCAL_BACKEND_PREFIXES = [
  "https://localhost:8004",
  "https://127.0.0.1:8004",
  "https://localhost:8001",
  "https://127.0.0.1:8001",
  "http://localhost:8001",
  "http://127.0.0.1:8001",
];

const runtimeState = {
  mixedContentDetected: false,
  mixedContentEvents: [],
  selectedBackendOrigin: "",
  selectedApiBase: "",
  allowHttpDevFallback: false,
};

function normalizeOrigin(value) {
  return String(value || "").trim().replace(/\/+$/, "");
}

function getEnv(name, legacyName = "") {
  const primary = String(process.env[name] || "").trim();
  if (primary) return primary;
  return legacyName ? String(process.env[legacyName] || "").trim() : "";
}

function boolEnv(name, legacyName = "", defaultValue = false) {
  const raw = getEnv(name, legacyName);
  if (!raw) return defaultValue;
  return ["1", "true", "yes", "on"].includes(raw.toLowerCase());
}

function parseAbsoluteUrl(value) {
  const raw = String(value || "").trim();
  if (!raw || raw.startsWith("/")) return null;
  try {
    return new URL(raw);
  } catch {
    return null;
  }
}

function toOriginFromApiBase(apiBase) {
  const parsed = parseAbsoluteUrl(apiBase);
  return parsed ? normalizeOrigin(parsed.origin) : "";
}

function isHttpsPage() {
  return typeof window !== "undefined" && String(window.location.protocol || "").toLowerCase() === "https:";
}

function getFrontendOrigin() {
  if (typeof window === "undefined") return "";
  return `${window.location.protocol}//${window.location.host}`;
}

function getBackendConfigInputs() {
  return {
    localBackendHttpsUrl: normalizeOrigin(
      getEnv("REACT_APP_LOCAL_BACKEND_HTTPS_URL", "REACT_APP_LOCAL_HTTPS_BACKEND") ||
        getEnv("REACT_APP_BACKEND_URL") ||
        DEFAULT_LOCAL_BACKEND_HTTPS_URL
    ),
    localBackendHttpUrl: normalizeOrigin(
      getEnv("REACT_APP_LOCAL_BACKEND_HTTP_URL", "REACT_APP_LOCAL_HTTP_BACKEND") ||
        getEnv("REACT_APP_LOCAL_BACKEND_URL") ||
        DEFAULT_LOCAL_BACKEND_HTTP_URL
    ),
    localGatewayHttpsUrl: normalizeOrigin(
      getEnv("REACT_APP_LOCAL_GATEWAY_HTTPS_URL") || DEFAULT_LOCAL_GATEWAY_HTTPS_URL
    ),
    tenantHostTemplate:
      getEnv("REACT_APP_TENANT_HOST") || getEnv("REACT_APP_TENANT_HOST_TEMPLATE") || DEFAULT_TENANT_HOST_TEMPLATE,
    publicBaseDomain: getEnv("REACT_APP_PUBLIC_BASE_DOMAIN", "REACT_APP_BASE_DOMAIN") || getBaseDomain(),
    allowHttpDevFallback: boolEnv(
      "REACT_APP_ALLOW_HTTP_BACKEND_FALLBACK",
      "REACT_APP_DEV_HTTP_BACKEND_FALLBACK",
      false
    ),
  };
}

function isSafeBackendCandidate(candidateOrigin, { frontendOrigin, isElectronRuntime, isLocalPage, isHttpsFrontend, allowHttpDevFallback }) {
  const normalized = normalizeOrigin(candidateOrigin);
  if (!normalized) return false;
  const parsed = parseAbsoluteUrl(normalized);
  if (!parsed) return false;
  const candidateIsLocal = isLocalHostname(parsed.hostname) || parsed.hostname === "::1";
  const frontendParsed = parseAbsoluteUrl(frontendOrigin);
  const frontendHostname = frontendParsed?.hostname || "";

  if (isElectronRuntime) {
    return parsed.protocol === "https:" && candidateIsLocal;
  }
  if (!isLocalPage) {
    return normalized === normalizeOrigin(frontendOrigin);
  }
  if (isHttpsFrontend && parsed.protocol !== "https:" && !allowHttpDevFallback) {
    return false;
  }
  if (frontendHostname && candidateIsLocal && !isLocalHostname(frontendHostname) && frontendHostname !== "::1") {
    return false;
  }
  return true;
}

function selectCandidateBackendOrigin() {
  if (typeof window === "undefined") {
    return {
      frontendOrigin: "",
      backendOrigin: "",
      apiBase: "/api",
      fileBase: "",
      isElectronRuntime: false,
      isLocalPage: false,
      isHttpsFrontend: false,
      allowHttpDevFallback: false,
    };
  }

  const {
    localBackendHttpsUrl,
    localBackendHttpUrl,
    localGatewayHttpsUrl,
    tenantHostTemplate,
    publicBaseDomain,
    allowHttpDevFallback,
  } = getBackendConfigInputs();
  const isElectronRuntime =
    Boolean(window.JEWEL_FLOW_ENV && window.JEWEL_FLOW_ENV.isElectron) || String(window.location.protocol || "").toLowerCase() === "file:";
  const frontendOrigin = getFrontendOrigin();
  const hostname = String(window.location.hostname || "").toLowerCase().trim();
  const isLocalPage = isLocalHostname(hostname) || hostname === "::1";
  const isHttpsFrontend = isHttpsPage();

  let backendOrigin = "";
  if (isElectronRuntime) {
    backendOrigin = localBackendHttpsUrl;
  } else if (isLocalPage) {
    // Audit: Detect if we are running on the gateway port vs a dev port (e.g. 3000)
    const gatewayParsed = parseAbsoluteUrl(localGatewayHttpsUrl);
    const gatewayPort = gatewayParsed?.port || (gatewayParsed?.protocol === "https:" ? "443" : "80");
    const currentPort = String(window.location.port || (window.location.protocol === "https:" ? "443" : "80"));

    if (currentPort === String(gatewayPort) || currentPort === "8013") {
      // ✅ STRICT GATEWAY MODE: Force same-origin and bypass environment overrides
      const apiBase = "/api"; 
      console.log(`[JewelFlow Runtime] Gateway mode detected (port ${currentPort}). Enforcing same-origin API base: ${apiBase}`);
      
      runtimeState.selectedBackendOrigin = frontendOrigin;
      runtimeState.selectedApiBase = apiBase;
      runtimeState.allowHttpDevFallback = allowHttpDevFallback;
      return {
        frontendOrigin,
        backendOrigin: frontendOrigin,
        apiBase,
        fileBase: frontendOrigin,
        isElectronRuntime: false,
        isLocalPage: true,
        isHttpsFrontend,
        allowHttpDevFallback,
        localBackendHttpsUrl,
        localBackendHttpUrl,
        localGatewayHttpsUrl,
        publicBaseDomain,
        tenantHostTemplate,
      };
    } else {
      // Fallback to direct backend for other local ports (like 3000)
      backendOrigin = isHttpsFrontend ? localBackendHttpsUrl : localBackendHttpUrl;
    }
  } else {
    backendOrigin = frontendOrigin;
  }

  const envApiBase = getEnv("REACT_APP_API_BASE");
  const envBackendUrl = getEnv("REACT_APP_BACKEND_URL");
  const envApiOrigin = toOriginFromApiBase(envApiBase);
  const envCandidates = [
    envApiOrigin ? { origin: envApiOrigin, apiBase: normalizeOrigin(envApiBase) } : null,
    envBackendUrl ? { origin: normalizeOrigin(envBackendUrl), apiBase: `${normalizeOrigin(envBackendUrl)}/api` } : null,
  ].filter(Boolean);

  for (const candidate of envCandidates) {
    if (
      isSafeBackendCandidate(candidate.origin, {
        frontendOrigin,
        isElectronRuntime,
        isLocalPage,
        isHttpsFrontend,
        allowHttpDevFallback,
      })
    ) {
      backendOrigin = candidate.origin;
      runtimeState.selectedBackendOrigin = backendOrigin;
      runtimeState.selectedApiBase = candidate.apiBase;
      runtimeState.allowHttpDevFallback = allowHttpDevFallback;
      return {
        frontendOrigin,
        backendOrigin,
        apiBase: candidate.apiBase,
        fileBase: backendOrigin,
        isElectronRuntime,
        isLocalPage,
        isHttpsFrontend,
        allowHttpDevFallback,
        localBackendHttpsUrl,
        localBackendHttpUrl,
        localGatewayHttpsUrl,
        publicBaseDomain,
        tenantHostTemplate,
      };
    }
  }

  const apiBase = backendOrigin ? `${backendOrigin}/api` : "/api";
  runtimeState.selectedBackendOrigin = backendOrigin;
  runtimeState.selectedApiBase = apiBase;
  runtimeState.allowHttpDevFallback = allowHttpDevFallback;
  return {
    frontendOrigin,
    backendOrigin,
    apiBase,
    fileBase: backendOrigin,
    isElectronRuntime,
    isLocalPage,
    isHttpsFrontend,
    allowHttpDevFallback,
    localBackendHttpsUrl,
    localBackendHttpUrl,
    localGatewayHttpsUrl,
    publicBaseDomain,
    tenantHostTemplate,
  };
}

export function getRuntimeOriginConfig() {
  return selectCandidateBackendOrigin();
}

export function resolveApiBaseUrl() {
  return getRuntimeOriginConfig().apiBase;
}

export function resolveFileBaseUrl() {
  return getRuntimeOriginConfig().fileBase;
}

export function recordMixedContentEvent(details) {
  runtimeState.mixedContentDetected = true;
  runtimeState.mixedContentEvents = [...runtimeState.mixedContentEvents.slice(-24), { ts: new Date().toISOString(), ...details }];
}

export function rebaseKnownLocalBackendUrl(raw, preferredBase) {
  const source = String(raw || "").trim();
  const targetBase = normalizeOrigin(preferredBase);
  if (!source || !targetBase) return source;
  for (const prefix of KNOWN_LOCAL_BACKEND_PREFIXES) {
    if (source.startsWith(prefix) && targetBase !== normalizeOrigin(prefix)) {
      return `${targetBase}${source.slice(prefix.length)}`;
    }
  }
  return source;
}

export function inspectRequestForMixedContent(rawUrl) {
  const urlValue = String(rawUrl || "").trim();
  if (!urlValue || typeof window === "undefined") {
    return { mixed: false, resolvedUrl: urlValue, reason: "" };
  }
  let resolvedUrl = urlValue;
  if (urlValue.startsWith("/")) {
    resolvedUrl = `${resolveApiBaseUrl().replace(/\/api$/, "")}${urlValue}`;
  }
  const parsed = parseAbsoluteUrl(resolvedUrl);
  const mixed = Boolean(parsed && isHttpsPage() && parsed.protocol === "http:");
  return {
    mixed,
    resolvedUrl,
    reason: mixed ? "https_frontend_to_http_backend" : "",
  };
}

export function getConnectivityDiagnosticsSnapshot() {
  const config = getRuntimeOriginConfig();
  return {
    frontendOrigin: config.frontendOrigin,
    frontendProtocol: typeof window !== "undefined" ? String(window.location.protocol || "") : "",
    selectedBackendUrl: config.backendOrigin,
    selectedApiBase: config.apiBase,
    localBackendHttpsUrl: config.localBackendHttpsUrl,
    localBackendHttpUrl: config.localBackendHttpUrl,
    localGatewayHttpsUrl: config.localGatewayHttpsUrl,
    publicBaseDomain: config.publicBaseDomain,
    tenantHostTemplate: config.tenantHostTemplate,
    allowHttpDevFallback: config.allowHttpDevFallback,
    mixedHttpCallsDetected: runtimeState.mixedContentDetected,
    mixedHttpCallCount: runtimeState.mixedContentEvents.length,
    recentMixedHttpCalls: runtimeState.mixedContentEvents,
  };
}
